// src/pages/products/components/bulk/ProductDataPreviewTable.jsx
import React, { useState, useEffect } from 'react';
import { Edit2, Check, Trash2, CheckCircle, XCircle } from 'lucide-react';
import styles from './ProductDataPreviewTable.module.css';

const ProductDataPreviewTable = ({ data, validationResults, onRemoveRow, onUpdateRow }) => {
  const [filter, setFilter] = useState('all');
  const [editingRowNumber, setEditingRowNumber] = useState(null);

  useEffect(() => {
    if (validationResults && validationResults.summary?.failureCount > 0) {
      setFilter('invalid');
    }
  }, [validationResults]);

  const getResult = (rowNumber) =>
    validationResults?.results.find(r => r.rowNumber === rowNumber);

  const getRowStatus = (rowNumber) => getResult(rowNumber)?.status || 'pending';
  const getRowErrors = (rowNumber) => getResult(rowNumber)?.errors || [];
  const getRowAction = (rowNumber) => getResult(rowNumber)?.action || null;

  const getFilteredData = () => {
    if (filter === 'all') return data;
    return data.filter(row => {
      const status = getRowStatus(row.rowNumber);
      if (filter === 'valid') return status === 'valid';
      if (filter === 'invalid') return status === 'failed';
      return true;
    });
  };

  const filteredData = getFilteredData();

  const getStatusCounts = () => {
    let validCount = 0, invalidCount = 0;
    data.forEach(row => {
      const status = getRowStatus(row.rowNumber);
      if (status === 'valid') validCount++;
      if (status === 'failed') invalidCount++;
    });
    return { validCount, invalidCount };
  };

  const { validCount, invalidCount } = getStatusCounts();

  const handleFieldChange = (rowNumber, field, value) => {
    onUpdateRow(rowNumber, field, value);
  };

  return (
    <div className={styles.dataPreview}>
      {/* Header / filter tabs */}
      <div className={styles.header}>
        <h3>Data Preview</h3>
        <div className={styles.stats}>
          <span
            className={`${styles.total} ${filter === 'all' ? styles.active : ''}`}
            onClick={() => setFilter('all')}
          >
            {data.length} rows
          </span>
          <span
            className={`${styles.valid} ${filter === 'valid' ? styles.active : ''}`}
            onClick={() => setFilter('valid')}
          >
            {validCount} valid
          </span>
          <span
            className={`${styles.invalid} ${filter === 'invalid' ? styles.active : ''}`}
            onClick={() => setFilter('invalid')}
          >
            {invalidCount} invalid
          </span>
        </div>
      </div>

      {/* Table */}
      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Row</th>
              <th>Status</th>
              <th>SKU</th>
              <th>Name</th>
              <th>Printable Name</th>
              <th>Price</th>
              <th>Category</th>
              <th>Brand</th>
              <th>Opening Stock</th>
              <th>Visibility</th>
              <th>Action</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.map((row) => {
              const status   = getRowStatus(row.rowNumber);
              const errors   = getRowErrors(row.rowNumber);
              const action   = getRowAction(row.rowNumber);
              const editable = status === 'failed';
              const isEditing = editingRowNumber === row.rowNumber;

              return (
                <React.Fragment key={row.rowNumber}>
                  <tr className={styles[status]}>
                    <td>{row.rowNumber}</td>

                    <td className={styles.statusCell}>
                      {status === 'valid'   && <CheckCircle size={16} className={styles.validIcon} />}
                      {status === 'failed'  && <XCircle     size={16} className={styles.invalidIcon} />}
                      {status === 'pending' && <span className={styles.pendingText}>-</span>}
                    </td>

                    {isEditing ? (
                      <>
                        <td>
                          <input
                            className={styles.cellInput}
                            value={row.sku || ''}
                            onChange={(e) => handleFieldChange(row.rowNumber, 'sku', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            className={styles.cellInput}
                            value={row.name || ''}
                            onChange={(e) => handleFieldChange(row.rowNumber, 'name', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            className={styles.cellInput}
                            value={row.printableName || ''}
                            onChange={(e) => handleFieldChange(row.rowNumber, 'printableName', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            className={styles.cellInput}
                            value={row.price ?? ''}
                            onChange={(e) => handleFieldChange(row.rowNumber, 'price', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            className={styles.cellInput}
                            value={row.category || ''}
                            onChange={(e) => handleFieldChange(row.rowNumber, 'category', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            className={styles.cellInput}
                            value={row.brand || ''}
                            onChange={(e) => handleFieldChange(row.rowNumber, 'brand', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            className={styles.cellInput}
                            value={row.openingStock ?? ''}
                            onChange={(e) => handleFieldChange(row.rowNumber, 'openingStock', e.target.value)}
                          />
                        </td>
                        <td>
                          <select
                            className={styles.cellInput}
                            value={row.visibility || 'public'}
                            onChange={(e) => handleFieldChange(row.rowNumber, 'visibility', e.target.value)}
                          >
                            <option value="public">public</option>
                            <option value="draft">draft</option>
                            <option value="trash">trash</option>
                          </select>
                        </td>
                      </>
                    ) : (
                      <>
                        <td>{row.sku || '-'}</td>
                        <td>{row.name || '-'}</td>
                        <td>{row.printableName || '-'}</td>
                        <td>{row.price ?? '-'}</td>
                        <td>{row.category || '-'}</td>
                        <td>{row.brand || '-'}</td>
                        <td>{row.openingStock ?? '-'}</td>
                        <td>{row.visibility || 'public'}</td>
                      </>
                    )}

                    <td>
                      {action === 'create' && <span className={styles.createBadge}>Create</span>}
                      {action === 'update' && <span className={styles.updateBadge}>Update</span>}
                      {!action && <span className={styles.pendingText}>-</span>}
                    </td>

                    <td>
                      <div className={styles.actionButtons}>
                        {isEditing ? (
                          <button
                            onClick={() => setEditingRowNumber(null)}
                            className={styles.doneButton}
                            title="Done editing"
                          >
                            <Check size={14} />
                          </button>
                        ) : editable && (
                          <button
                            onClick={() => setEditingRowNumber(row.rowNumber)}
                            className={styles.editButton}
                            title="Edit row"
                          >
                            <Edit2 size={14} />
                          </button>
                        )}
                        <button
                          onClick={() => onRemoveRow(row.rowNumber)}
                          className={styles.deleteButton}
                          title="Remove row"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>

                  {errors.length > 0 && (
                    <tr className={styles.errorRow}>
                      <td colSpan="12">
                        <div className={styles.errors}>
                          {errors.map((error, idx) => (
                            <span key={idx} className={styles.errorItem}>{error}</span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>

        {filteredData.length === 0 && (
          <div className={styles.noData}>
            <p>No {filter !== 'all' ? filter : ''} rows to display</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDataPreviewTable;
